import superagent from '@ott/superagent';

import { useQuery } from '@tanstack/react-query';

import { FIELDS } from '../constants';
import {
  groupSuggestionsByCategories,
  groupSuggestionsByCountries,
} from '../helpers/prepareSuggestions';
import trackError from '../helpers/trackError';
import { GeoAutocompleteOption, GeoPoint, SearchForm, ServerSuggestion } from '../types';
import { useDebounce } from './useDebounce';
import useIsChanged from './useIsChanged';
import useSearchHistory from './useSearchHistory';
import useSuggestionStatistic from './useSuggestionStatistic';
import useSuggestionsManager from './useSuggestionsManager';

const SUGGESTIONS_URL = '/_bus/geo/suggest';

const suggestionsAdapter = (response: { data?: ServerSuggestion[] }) => {
  return response.data?.map((item): GeoAutocompleteOption => {
    return {
      country: item.country,
      district: item.district,
      value: item.target.id,
      label: item.target.entity.name,
      region: item.region,
      translit: item.target.entity.trnslt,
      type: item.type,
      targetType: item.target.type,
      entityType: item.target.entity.typeName,
      geoPoint: {
        id: item.geopoint.id,
        name: item.geopoint.name,
        translit: item.trnslt,
        district: item.district,
        region: item.region,
        country: item.country,
      },
    };
  });
};

const commonConfig = {
  retry: false,
  refetchOnWindowFocus: false,
  onError: (error: Error) => {
    trackError(error);
  },
};

const commonQuery = { limit: 17, includeStations: true };

const fetchDefaultSuggestions = async () => {
  const response: GeoAutocompleteOption[] = await superagent
    .get(SUGGESTIONS_URL)
    .query(commonQuery)
    .then((res: Response) => res.body)
    .then(suggestionsAdapter);

  return response;
};

const fetchSuggestions = async ({
  input,
  departureGeoPointId,
}: {
  input?: string;
  departureGeoPointId?: GeoPoint['id'];
}): Promise<GeoAutocompleteOption[]> => {
  return superagent
    .get(SUGGESTIONS_URL)
    .query({ query: input, departure_geo_id: departureGeoPointId, ...commonQuery })
    .then((res: Response) => res.body)
    .then(suggestionsAdapter);
};

const useSuggestions = ({
  isDepartureOpen,
  isDestinationOpen,
  departureInput,
  destinationInput,
  departureId,
  departureGeoPointId,
  destinationId,
}: {
  isDepartureOpen: boolean;
  isDestinationOpen: boolean;
  departureInput?: SearchForm['departure'];
  destinationInput?: SearchForm['destination'];
  departureId?: SearchForm['departureOption']['value'];
  departureGeoPointId?: GeoPoint['id'];
  destinationId?: SearchForm['destinationOption']['value'];
}) => {
  // Добавил useDebounce, чтобы сократить количество обращений к бэку при быстром наборе текста в инпуте
  const debouncedDepartureInput = useDebounce(departureInput);
  const debouncedDestinationInput = useDebounce(destinationInput);

  const { isDepartureIdChanged, isDestinationIdChanged } = useIsChanged({
    departureId,
    destinationId,
  });

  const { departureHistory, destinationHistory } = useSearchHistory();

  // Дефолтные саджесты пункта отправления. Получаем только один раз в самом начале
  const { data: defaultDepartureSuggestions, isFetching: areDefaultDepartureSuggestionsLoading } =
    useQuery({
      queryKey: ['defaultDepartureSuggestions'],
      queryFn: fetchDefaultSuggestions,
      select: (suggestions) => groupSuggestionsByCategories(suggestions, departureHistory),
      initialData: [],
      refetchOnMount: 'always',
      ...commonConfig,
    });

  // Дефолтные саджесты пункта назначения. Получаем только один раз в самом начале
  const {
    data: defaultDestinationSuggestions,
    isFetching: areDefaultDestinationSuggestionsLoading,
  } = useQuery({
    queryKey: ['defaultDestinationSuggestions'],
    queryFn: fetchDefaultSuggestions,
    select: (suggestions) => groupSuggestionsByCategories(suggestions, destinationHistory),
    initialData: [],
    refetchOnMount: 'always',
    ...commonConfig,
  });

  // Получение саджестов пункта отправления при вводе текста в инпут
  const { data: departureSuggestions, isFetching: areDepartureSuggestionsLoading } = useQuery({
    queryKey: ['departureSuggestions', debouncedDepartureInput],
    queryFn: () => fetchSuggestions({ input: debouncedDepartureInput }),
    select: (suggestions) => groupSuggestionsByCountries(suggestions),
    initialData: [],
    enabled: isDepartureOpen && !!debouncedDepartureInput && !isDepartureIdChanged,
    refetchOnMount: false,
    keepPreviousData: true,
    ...commonConfig,
  });

  // Получение саджестов пункта назначения при вводе текста в инпут
  const { data: destinationSuggestions, isFetching: areDestinationSuggestionsLoading } = useQuery({
    queryKey: ['destinationSuggestions', debouncedDestinationInput],
    queryFn: () => fetchSuggestions({ input: debouncedDestinationInput }),
    select: (suggestions) => groupSuggestionsByCountries(suggestions),
    initialData: [],
    enabled: isDestinationOpen && !!debouncedDestinationInput && !isDestinationIdChanged,
    refetchOnMount: false,
    keepPreviousData: true,
    ...commonConfig,
  });

  // Получение саджестов пункта назначения на основании выбора пункта отправления
  const {
    data: destinationSuggestionsByDepartureId,
    isFetching: areDestinationSuggestionsByDepartureIdLoading,
  } = useQuery({
    queryKey: ['destinationSuggestionsByDepartureId', departureId],
    queryFn: () => fetchSuggestions({ departureGeoPointId }),
    select: (suggestions) => groupSuggestionsByCategories(suggestions, destinationHistory),
    initialData: [],
    refetchOnMount: false,
    keepPreviousData: false,
    ...commonConfig,
  });

  const {
    departureSuggestionsToShow,
    destinationSuggestionsToShow,
    isDepartureInEditMode,
    isDestinationInEditMode,
  } = useSuggestionsManager({
    isDepartureOpen,
    isDestinationOpen,
    departureInput: debouncedDepartureInput,
    destinationInput: debouncedDestinationInput,
    defaultDepartureSuggestions,
    defaultDestinationSuggestions,
    departureSuggestions,
    destinationSuggestions,
    destinationSuggestionsByDepartureId,
    departureId,
  });

  useSuggestionStatistic({
    direction: FIELDS.DEPARTURE,
    isOpen: isDepartureOpen,
    isEditMode: isDepartureInEditMode,
    suggestions: departureSuggestions,
    suggestionsToShow: departureSuggestionsToShow,
    debouncedInput: debouncedDepartureInput,
    isLoading: areDefaultDepartureSuggestionsLoading || areDepartureSuggestionsLoading,
  });

  useSuggestionStatistic({
    direction: FIELDS.DESTINATION,
    isOpen: isDestinationOpen,
    isEditMode: isDestinationInEditMode,
    suggestions: destinationSuggestions,
    suggestionsToShow: destinationSuggestionsToShow,
    debouncedInput: debouncedDestinationInput,
    isLoading:
      areDefaultDestinationSuggestionsLoading ||
      areDestinationSuggestionsLoading ||
      areDestinationSuggestionsByDepartureIdLoading,
  });

  return {
    departureSuggestions: departureSuggestionsToShow,
    destinationSuggestions: destinationSuggestionsToShow,
    areDefaultDepartureSuggestionsLoading,
    areDefaultDestinationSuggestionsLoading,
    areDepartureSuggestionsLoading,
    areDestinationSuggestionsLoading,
    areDestinationSuggestionsByDepartureIdLoading,
  };
};

export default useSuggestions;
